/**
 * Settings → Client portal (superadmin), T-045: what a client's certificate of insurance must show.
 *
 *   - Certificate holders, one per Hollywood Trucks entity, filled in from one of the three
 *     companies or typed. Each is also the additional insured and loss payee unless either is
 *     set apart (L1). The entity comes from Sales' quote (by the booking's location); an entity
 *     left empty shows clients "contact us", and the launch line names it (T-055).
 *   - Coverage limits: a default plus optional per-region overrides (Sales rate-card regions). An
 *     empty box in an override falls back to the default. Limits never block a booking.
 *
 * Nothing is live until it is saved here: "Use the approved values" only fills the form. Save sends
 * only what changed (the server merges holders and default lines), so a second superadmin's edits
 * to other parts survive; every save is audited on the server.
 *
 * L2: each card ends with who changed it last and when, plus a short history, read once from those
 * audit rows (one read for the page, reloaded after each save here or in the agreement section).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CoiHolderFields } from '@/components/settings/portal/CoiHolderFields';
import { CoverageFields } from '@/components/settings/portal/CoverageFields';
import { RentalAgreementSettings } from '@/components/settings/portal/RentalAgreementSettings';
import { CardHistory } from '@/components/settings/portal/SettingsHistory';
import { entriesFor, useSettingsHistory } from '@/hooks/useSettingsHistory';
import { historyKey } from '@/lib/portalSettingsHistoryApi';
import { getCoiSettings, saveCoiSettings, type CoiSettingsResponse, type EntityKey } from '@/lib/portalCoiSettingsApi';
import { COVERAGE_ORDER, ENTITY_ORDER, draftErrors, emptyCoverage, isDirty, launchGaps, toDraft, toPatch, type CoiDraft } from '@/lib/coiSettingsDraft';

/** The Inc.'s card: the California LLC is Eric's decision; the broker check is advice, not a gate. */
const INC_NOTE = 'The approved certificate holder for Hollywood Trucks Technologies, Inc. bookings is the California LLC. Approved by Eric 10/29; confirm with your insurance broker when convenient.';

/** "California — West Coast bookings…" → "California", for the launch line. */
const shortLabel = (label: string) => label.split(' — ')[0];

/** "A", "A and B", "A, B and C". */
function listOf(items: string[]): string {
  if (items.length < 2) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** T-055: one line saying what, if anything, keeps the saved COI requirements from launch. */
function launchLine(missing: string[], limitsMissing: boolean): string {
  if (!missing.length && !limitsMissing) return 'Every company has a certificate holder and coverage limits saved.';
  const parts = [
    ...(missing.length ? [`no certificate holder is saved for ${listOf(missing)}`] : []),
    ...(limitsMissing ? ['no coverage limits are saved'] : []),
  ];
  const text = `Before launch: ${parts.join(', and ')}`;
  // A list ending in "Inc." already has its period.
  return text.endsWith('.') ? text : `${text}.`;
}

function SectionTitle({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <h3 className="text-sm font-semibold text-foreground">{children}</h3>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const PERMISSION_RE = /forbidden|superadmin|admin access|not authori[sz]ed/i;

export function ClientPortalSettings() {
  const [data, setData] = useState<CoiSettingsResponse | null>(null);
  const [draft, setDraftState] = useState<CoiDraft | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const { history, reload: reloadHistory } = useSettingsHistory();
  const historyFor = (label: string, key: string) => (
    <CardHistory label={label} entries={entriesFor(history, key)} unavailable={history.unavailable} />
  );

  // Any edit clears the last validation message: it described the form as it was.
  const setDraft = useCallback((next: CoiDraft) => { setDraftState(next); setErrors([]); }, []);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await getCoiSettings();
      setData(res);
      setDraftState(toDraft(res.config));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'The settings could not be loaded.');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const labels = useMemo(
    () => Object.fromEntries((data?.entities ?? []).map((e) => [e.key, e.label])) as Partial<Record<EntityKey, string>>,
    [data],
  );
  const regionName = useMemo(() => new Map((data?.regions ?? []).map((r) => [r.id, r.name])), [data]);

  if (loadError) {
    if (PERMISSION_RE.test(loadError)) {
      return <div className="p-6 text-sm text-muted-foreground">Client portal settings are available to superadmins.</div>;
    }
    return (
      <div className="flex flex-col items-start gap-3 p-6 text-sm text-muted-foreground">
        <p role="alert">The client portal settings couldn’t be loaded. {loadError}</p>
        <Button type="button" variant="outline" onClick={() => void load()}>Try again</Button>
      </div>
    );
  }
  if (!data || !draft) {
    return (
      <div className="flex flex-1 items-center justify-center p-10 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> Loading…
      </div>
    );
  }

  const dirty = isDirty(draft, data.config);
  const addable = data.regions.filter((r) => !draft.overrides[r.id]);
  /** A card's name: the Sales region's, or an honest stand-in with a short id to tell cards apart. */
  const overrideLabel = (id: string) => regionName.get(id)
    ?? (data.regions_unavailable ? `Region (name unavailable) · …${id.slice(-6)}` : `Region no longer listed in Sales · …${id.slice(-6)}`);
  const overrideIds = Object.keys(draft.overrides).sort((a, b) => overrideLabel(a).localeCompare(overrideLabel(b)));
  const limitsAreApproved = COVERAGE_ORDER.every((k) => draft.defaults[k].trim() === data.suggested.limits[k].trim());
  const gaps = launchGaps(data.config, ENTITY_ORDER);
  const ready = !gaps.missingHolders.length && !gaps.limitsMissing;

  const save = async () => {
    const problems = draftErrors(draft, labels);
    if (problems.length) {
      setErrors(problems);
      return;
    }
    const patch = toPatch(draft, data.config);
    if (!patch.holders && !patch.limits) {
      // Only changes that save as what is already saved (e.g. a party set to the holder itself).
      setDraftState(toDraft(data.config));
      return;
    }
    setSaving(true);
    try {
      const { config } = await saveCoiSettings(patch);
      setData({ ...data, config });
      setDraftState(toDraft(config));
      toast.success('COI requirements saved.');
      void reloadHistory();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'The settings could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex max-w-4xl flex-col gap-6 p-4 md:p-6">
      <header className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-muted/40 text-[#8FB560]">
          <ShieldCheck className="h-5 w-5" aria-hidden />
        </div>
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Client portal</h2>
          <p className="text-sm text-muted-foreground">What a client’s certificate of insurance must show, and the rental agreement they e-sign. Clients see both on their booking.</p>
        </div>
      </header>

      {/* Locked while saving, so nothing typed during the save is overwritten by its result. */}
      <fieldset disabled={saving} className="flex min-w-0 flex-col gap-6">
        <Card>
          <CardContent className="flex flex-col gap-4 p-4 md:p-6">
            <SectionTitle hint="Who each entity’s bookings name as certificate holder, additional insured and loss payee. A booking’s entity follows its location, the same as on its quote.">
              Certificate holders
            </SectionTitle>
            <p className={`text-xs ${ready ? 'text-muted-foreground' : 'text-foreground'}`}>
              {launchLine(gaps.missingHolders.map((k) => shortLabel(labels[k] ?? k)), gaps.limitsMissing)}
            </p>
            {ENTITY_ORDER.map((key) => (
              <CoiHolderFields
                key={key}
                id={`holder-${key}`}
                label={labels[key] ?? key}
                value={draft.holders[key]}
                saved={data.config.holders[key]}
                suggested={data.suggested.holders[key]}
                parties={data.parties ?? []}
                note={key === 'louisiana' ? INC_NOTE : undefined}
                footer={historyFor(labels[key] ?? key, historyKey.holder(key))}
                onChange={(next) => setDraft({ ...draft, holders: { ...draft.holders, [key]: next } })}
              />
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-4 p-4 md:p-6">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <SectionTitle hint="A line left empty isn’t shown. Until something is saved, clients see no limits. Limits never block a booking.">
                Coverage limits
              </SectionTitle>
              {!limitsAreApproved && (
                <Button type="button" variant="outline" size="sm" aria-label="Use the approved values for coverage limits" onClick={() => setDraft({ ...draft, defaults: { ...data.suggested.limits } })}>
                  Use the approved values
                </Button>
              )}
            </div>
            <CoverageFields id="limits-default" fields={data.fields} value={draft.defaults} onChange={(next) => setDraft({ ...draft, defaults: next })} />
            {historyFor('Coverage limits', historyKey.limits)}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-4 p-4 md:p-6">
            <SectionTitle hint="Only for a region whose limits differ. An empty box uses the default above.">Region overrides</SectionTitle>
            {data.regions_unavailable && (
              <p role="status" className="text-xs text-muted-foreground">Sales’ regions couldn’t be loaded right now, so region names are missing and new overrides can’t be added. Saved overrides are kept.</p>
            )}
            {overrideIds.map((id) => (
              <section key={id} aria-label={overrideLabel(id)} className="flex flex-col gap-3 rounded-lg border border-border p-4">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold">{overrideLabel(id)}</h4>
                  <Button type="button" variant="ghost" size="sm" aria-label={`Remove override: ${overrideLabel(id)}`} onClick={() => {
                    const next = { ...draft.overrides };
                    delete next[id];
                    setDraft({ ...draft, overrides: next });
                  }}>Remove override</Button>
                </div>
                <CoverageFields
                  id={`limits-${id}`}
                  fields={data.fields}
                  value={draft.overrides[id]}
                  placeholders={draft.defaults}
                  onChange={(next) => setDraft({ ...draft, overrides: { ...draft.overrides, [id]: next } })}
                />
              </section>
            ))}
            {addable.length > 0 && (
              <div className="flex max-w-sm flex-col gap-1.5">
                <Select value="" onValueChange={(id) => setDraft({ ...draft, overrides: { ...draft.overrides, [id]: emptyCoverage() } })}>
                  <SelectTrigger aria-label="Add an override for a region"><SelectValue placeholder="Add an override for a region…" /></SelectTrigger>
                  <SelectContent>
                    {addable.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {historyFor('Region overrides', historyKey.regions)}
          </CardContent>
        </Card>
      </fieldset>

      {errors.length > 0 && (
        <div role="alert" className="flex flex-col gap-1 text-sm text-[#E0556C]">
          {errors.map((e) => <p key={e}>{e}</p>)}
        </div>
      )}
      <div className="flex items-center gap-3">
        <Button type="button" className="h-11" disabled={!dirty || saving} onClick={save}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
          Save
        </Button>
        {!dirty && <span className="text-xs text-muted-foreground">No unsaved changes.</span>}
      </div>

      {/* Saved on its own, a change at a time (each audited), so it sits apart from the COI Save. */}
      <RentalAgreementSettings history={history} onChanged={() => void reloadHistory()} />
    </div>
  );
}
